import { useNavigate } from "react-router-dom";
import { printCenterLink } from "../admin/print/pdfClient";
import React, { useEffect, useMemo, useState } from "react";
import { Printer, Boxes, Check, Tag, FolderPlus, FolderInput, Search, X, Loader2, ListChecks, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/* Pack & Ship folders (Jun 2026). Only Kayla's own folders appear as
   queues , orders she hasn't filed sit in a single "Unfiled" bucket.
   Printing a folder produces a full packing slip per order (ALL order
   information) plus an invoice page per order. Shown on both the Packing
   and Shipping tabs. */

export const UNFILED = "Unfiled";

export const categoryOf = (o) => ((o.pack_folder || "").trim()) || UNFILED;

export function PackShipPanel({ orders, onReload, selectedFolders, toggleFolder, selectedIds, stage }) {
  const navigate = useNavigate();
  const [showFiling, setShowFiling] = useState(false);
  const [skipPacked, setSkipPacked] = useState(false);
  const [sending, setSending] = useState(false);
  const [folders, setFolders] = useState([]);
  const [fileTo, setFileTo] = useState("");
  const [newFolderName, setNewFolderName] = useState("");

  const loadFolders = () => api.get("/console/pack-folders").then((r) => setFolders(r.data?.folders || [])).catch(() => {});
  useEffect(() => { loadFolders(); }, [orders.length]);

  const fileSelected = async (target = fileTo) => {
    const ids = orders.filter((o) => selectedIds.has(o.id)).map((o) => o.id);
    if (ids.length === 0) return;
    setSending(true);
    try {
      let folderId = target;
      if (target === "__new__") {
        const name = newFolderName.trim();
        if (!name) { toast.error("Type a name for the new folder."); return; }
        const r = await api.post("/console/pack-folders", { name });
        folderId = r.data?.id;
      }
      const r = await api.post("/console/pack-folders/assign", { order_ids: ids, folder_id: folderId || null });
      toast.success(folderId
        ? `${r.data?.assigned} order${r.data?.assigned === 1 ? "" : "s"} moved into “${r.data?.folder}”.`
        : `${r.data?.unfiled} order${r.data?.unfiled === 1 ? "" : "s"} taken out of their folder.`);
      setNewFolderName(""); setFileTo("");
      await loadFolders();
      if (onReload) await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't move those orders."); }
    finally { setSending(false); }
  };

  const categories = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const c = categoryOf(o);
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(o);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === UNFILED) return 1;
      if (b[0] === UNFILED) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [orders]);

  const allOn = selectedFolders.size === categories.length && categories.length > 0;
  const toggleAll = () => {
    if (allOn) {
      categories.forEach(([c, os]) => selectedFolders.has(c) && toggleFolder(c, os.map((o) => o.id)));
    } else {
      categories.forEach(([c, os]) => !selectedFolders.has(c) && toggleFolder(c, os.map((o) => o.id)));
    }
  };

  // Printing / sending always follows the individually selected orders , the
  // folder chips are just a fast way to tick a whole queue on or off.
  const printable = useMemo(() => {
    const groups = categories
      .map(([c, os]) => [c, os.filter((o) => selectedIds.has(o.id) && (!skipPacked || !o.packed_at))])
      .filter(([, os]) => os.length > 0);
    return groups;
  }, [categories, selectedIds, skipPacked]);
  const chosenOrders = printable.flatMap(([, os]) => os);
  const selectedCount = orders.filter((o) => selectedIds.has(o.id)).length;

  const sendToPullList = async () => {
    const ids = orders.filter((o) => selectedIds.has(o.id)).map((o) => o.id);
    if (ids.length === 0) return;
    setSending(true);
    try {
      const r = await api.post("/admin/pull-list/add", { order_ids: ids });
      const added = r.data?.added || 0;
      toast.success(added
        ? `${added} order${added === 1 ? "" : "s"} sent to the Pull List.`
        : "Those orders are already on the Pull List.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send to the Pull List."); }
    finally { setSending(false); }
  };

  const sendTo = async (stage) => {
    const ids = orders.filter((o) => selectedIds.has(o.id)).map((o) => o.id);
    if (ids.length === 0) return;
    setSending(true);
    try {
      for (const id of ids) await api.post(`/console/orders/${id}/advance`, { target: stage });
      toast.success(`${ids.length} order${ids.length === 1 ? "" : "s"} sent to ${stage === "delivery" ? "Delivery" : "Shipping"}.`);
      if (onReload) await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't move those orders."); }
    finally { setSending(false); }
  };

  if (orders.length === 0) return null;

  return (
    <div className="mb-5 bg-white border rounded-md p-4" style={{ borderColor: "#E8DFCB" }} data-testid="pack-ship-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">
          <Boxes className="w-4 h-4" /> Your folders
          <span className="tracking-normal normal-case font-normal italic text-[#9C8B73]" data-testid="pack-selected-count">
            {selectedCount} order{selectedCount === 1 ? "" : "s"} selected
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#3C2A1E] cursor-pointer select-none">
            <input type="checkbox" checked={skipPacked} onChange={(e) => setSkipPacked(e.target.checked)} data-testid="pack-skip-packed" />
            Skip packed
          </label>
          <button type="button" onClick={() => setShowFiling((v) => !v)} data-testid="pack-folders-toggle"
            className="px-3 h-8 rounded border text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5"
            style={showFiling ? { backgroundColor: "#3E1F46", borderColor: "#3E1F46", color: "#fff" } : { borderColor: "#E8DFCB", color: "#3C2A1E" }}>
            <FolderPlus className="w-3.5 h-3.5" /> Folders
          </button>
          <button type="button" onClick={toggleAll} data-testid="pack-select-all"
            className="px-3 h-8 rounded border text-[10px] tracking-[0.2em] uppercase font-semibold"
            style={{ borderColor: "#E8DFCB", color: "#3C2A1E" }}>
            {allOn ? "Clear all" : "Select all"}
          </button>
          {stage === "assigned" && (
            <button type="button" disabled={selectedCount === 0 || sending}
              onClick={sendToPullList}
              data-testid="pack-send-pull-list"
              title="Send the selected orders to the Pull List"
              className="px-4 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ backgroundColor: "#3E1F46" }}>
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
              Send to Pull List{selectedCount ? ` (${selectedCount})` : ""}
            </button>
          )}
          {stage === "assigned" && (
            <>
              <button type="button" disabled={selectedCount === 0 || sending} onClick={() => sendTo("shipping")}
                data-testid="pack-send-shipping" title="Send the selected packed orders to Shipping"
                className="px-4 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ backgroundColor: "#6E1E3A" }}>
                <Truck className="w-3.5 h-3.5" /> To shipping
              </button>
              <button type="button" disabled={selectedCount === 0 || sending} onClick={() => sendTo("delivery")}
                data-testid="pack-send-delivery" title="Send the selected packed orders to the Delivery run"
                className="px-4 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ backgroundColor: "#1F5B3F" }}>
                <MapPin className="w-3.5 h-3.5" /> To delivery
              </button>
            </>
          )}
          <button type="button" disabled={chosenOrders.length === 0}
            onClick={() => navigate(printCenterLink("address_labels", printable.map((o) => o.id)))}
            data-testid="pack-print-labels" title="Opens Documents & Print with these orders selected"
            className="px-4 h-8 rounded border text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ borderColor: "#6E1E3A", color: "#6E1E3A" }}>
            <Tag className="w-3.5 h-3.5" /> Print address labels
          </button>
          <button type="button" disabled={chosenOrders.length === 0}
            onClick={() => navigate(printCenterLink("__batch", printable.map((o) => o.id)))}
            data-testid="pack-print-summary" title="Opens Documents & Print , a packing batch with slips, invoices, checklist, pull list and labels"
            className="px-4 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ backgroundColor: "#6E1E3A" }}>
            <Printer className="w-3.5 h-3.5" /> Print packing batch{chosenOrders.length ? ` (${chosenOrders.length})` : ""}
          </button>
        </div>
      </div>
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded border" style={{ borderColor: "#C9A268", backgroundColor: "#FBF6E9" }} data-testid="pack-move-bar">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[#3E1F46]">
            <FolderInput className="w-4 h-4" /> Move {selectedCount} selected into
          </span>
          <select value={fileTo} onChange={(e) => setFileTo(e.target.value)} data-testid="pack-move-select"
            className="h-8 px-2 border rounded text-xs bg-white" style={{ borderColor: "#E8DFCB" }}>
            <option value="">Choose folder…</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.count})</option>)}
            <option value="__new__">+ New folder…</option>
          </select>
          {fileTo === "__new__" && (
            <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fileSelected("__new__")}
              placeholder="New folder name" data-testid="pack-move-new-name"
              className="h-8 px-3 border rounded text-xs bg-white outline-none w-44" style={{ borderColor: "#E8DFCB" }} />
          )}
          <button type="button" onClick={fileSelected} disabled={sending || !fileTo} data-testid="pack-move-go"
            className="px-3 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ backgroundColor: "#3E1F46" }}>
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />} Move
          </button>
          <button type="button" onClick={() => fileSelected(null)} disabled={sending} data-testid="pack-move-unfile"
            className="px-3 h-8 rounded border text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-40"
            style={{ borderColor: "#A33030", color: "#A33030" }}>Take out of folder</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {categories.map(([c, os]) => {
          const on = selectedFolders.has(c);
          const packed = os.filter((o) => o.packed_at).length;
          const done = packed === os.length;
          const picked = os.filter((o) => selectedIds.has(o.id)).length;
          return (
            <button key={c} type="button" onClick={() => toggleFolder(c, os.map((o) => o.id))}
              data-testid={`pack-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              title="Filter the list to this folder and select its orders"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors"
              style={on ? { backgroundColor: "#6E1E3A", borderColor: "#6E1E3A", color: "#fff" } : { borderColor: done ? "#2F6B4F" : "#E8DFCB", color: "#3C2A1E" }}>
              {on && <Check className="w-3 h-3" />} {c}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${on ? "bg-white/20 text-white" : done ? "bg-[#2F6B4F] text-white" : "bg-[#F3EBDD] text-[#6E1E3A]"}`} data-testid={`pack-progress-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                {packed}/{os.length} packed{done ? " ✓" : ""}
              </span>
              {picked > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${on ? "bg-white/20 text-white" : "bg-[#3E1F46] text-white"}`}>
                  {picked} picked
                </span>
              )}
            </button>
          );
        })}
        {selectedFolders.size > 0 && (
          <span className="inline-flex items-center text-[11px] italic text-[#6E1E3A]" data-testid="pack-folder-filter-note">
            Showing only {[...selectedFolders].join(", ")}
          </span>
        )}
      </div>
      <div className="mt-2 text-[11px] italic text-[#9C8B73]">Click a folder to filter the list and select every order in it, or tick orders individually below. Selected orders are what gets printed or sent to the Pull List.</div>
      {showFiling && <PackFolderFiling orders={orders} onReload={onReload} />}
    </div>
  );
}

function PackFolderFiling({ orders, onReload }) {
  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState("");
  const [kw, setKw] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState("");
  const [busy, setBusy] = useState(false);

  const loadFolders = () => api.get("/console/pack-folders").then((r) => setFolders(r.data?.folders || [])).catch(() => {});
  useEffect(() => { loadFolders(); }, []);

  const matches = useMemo(() => {
    const needle = kw.trim().toLowerCase();
    if (!needle) return [];
    return orders.filter((o) => [
      o.customer_name, o.email, o.order_number, o.kind, o.pack_folder,
      ...(o.line_items || []).map((i) => i.name),
    ].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [orders, kw]);

  const allPicked = matches.length > 0 && matches.every((o) => picked.has(o.id));
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pickAll = () => setPicked(allPicked ? new Set() : new Set(matches.map((o) => o.id)));

  const createFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await api.post("/console/pack-folders", { name });
      setNewFolder("");
      await loadFolders();
      setAssignTo(r.data?.id || "");
      toast.success(`Folder “${r.data?.name}” ready.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create folder."); }
    finally { setBusy(false); }
  };

  const removeFolder = async (f) => {
    if (!window.confirm(`Delete folder “${f.name}”? Its orders go back to the automatic queues.`)) return;
    setBusy(true);
    try {
      await api.delete(`/console/pack-folders/${f.id}`);
      await loadFolders();
      onReload?.();
      toast.success(`Folder “${f.name}” deleted.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete folder."); }
    finally { setBusy(false); }
  };

  const assign = async (folderId) => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/console/pack-folders/assign", { order_ids: [...picked], folder_id: folderId || null });
      toast.success(folderId ? `${r.data?.assigned} order${r.data?.assigned === 1 ? "" : "s"} filed into “${r.data?.folder}”.` : `${r.data?.unfiled} order${r.data?.unfiled === 1 ? "" : "s"} removed from folders.`);
      setPicked(new Set());
      await loadFolders();
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't file the orders."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t" style={{ borderColor: "#E8DFCB" }} data-testid="pack-folder-filing">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#3E1F46]">
          <FolderInput className="w-4 h-4" /> Folders
        </span>
        <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()}
          placeholder="New folder name…" data-testid="pack-folder-name-input"
          className="h-8 px-3 border rounded text-xs bg-white outline-none w-44" style={{ borderColor: "#E8DFCB" }} />
        <button type="button" onClick={createFolder} disabled={busy || !newFolder.trim()} data-testid="pack-folder-create"
          className="px-3 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-40"
          style={{ backgroundColor: "#3E1F46" }}>Create</button>
        {folders.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border text-xs font-semibold"
            style={{ borderColor: "#3E1F46", color: "#3E1F46" }} data-testid={`pack-folder-chip-${f.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {f.name} <span className="opacity-60">× {f.count}</span>
            <button type="button" onClick={() => removeFolder(f)} title="Delete folder" className="p-0.5 rounded-full hover:bg-[#3E1F46] hover:text-white transition-colors">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 px-3 h-9 bg-white border rounded max-w-md mb-2" style={{ borderColor: "#E8DFCB" }}>
        <Search className="w-4 h-4 text-[#9C8B73]" />
        <input value={kw} onChange={(e) => { setKw(e.target.value); setPicked(new Set()); }} placeholder="Search orders by keyword (name, item, order #)…"
          data-testid="pack-folder-search" className="flex-1 outline-none text-sm bg-transparent" />
      </div>
      {kw.trim() && (
        matches.length === 0 ? <div className="text-xs italic text-[#7A6A55] py-2">No packing orders match “{kw.trim()}”.</div> : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3C2A1E] cursor-pointer">
              <input type="checkbox" checked={allPicked} onChange={pickAll} data-testid="pack-folder-select-all" />
              Select all {matches.length} match{matches.length === 1 ? "" : "es"}
            </label>
            {picked.size > 0 && (
              <>
                <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="pack-folder-assign-select"
                  className="h-8 px-2 border rounded text-xs bg-white" style={{ borderColor: "#E8DFCB" }}>
                  <option value="">Choose folder…</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <button type="button" onClick={() => assign(assignTo)} disabled={busy || !assignTo} data-testid="pack-folder-assign"
                  className="px-3 h-8 rounded text-white text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                  style={{ backgroundColor: "#3E1F46" }}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />} File {picked.size} into folder
                </button>
                <button type="button" onClick={() => assign(null)} disabled={busy} data-testid="pack-folder-unfile"
                  className="px-3 h-8 rounded border text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-40"
                  style={{ borderColor: "#A33030", color: "#A33030" }}>Remove from folder</button>
              </>
            )}
          </div>
          <div className="max-h-56 overflow-auto border rounded bg-white divide-y" style={{ borderColor: "#E8DFCB" }}>
            {matches.map((o) => (
              <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer hover:bg-[#FBF6E9]" data-testid={`pack-folder-row-${o.id}`}>
                <input type="checkbox" checked={picked.has(o.id)} onChange={() => togglePick(o.id)} />
                <strong className="text-[#3C2A1E]">{o.customer_name || o.email || "Unknown"}</strong>
                <span className="opacity-60">#{o.order_number || String(o.id || "").slice(0, 8)}</span>
                <span className="truncate flex-1 opacity-70">{(o.line_items || []).map((li) => li.name).join(", ")}</span>
                <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: "#F3EBDD", color: "#6E1E3A" }}>{categoryOf(o)}</span>
              </label>
            ))}
          </div>
        </>
      ))}
    </div>
  );
}

function wavyRect(w, h, amp = 4.5, step = 26, inset = 8) {
  let d = `M ${inset} ${inset}`;
  let i = 0;
  for (let x = inset; x < w - inset; x += step, i++) {
    const nx = Math.min(x + step, w - inset);
    d += ` Q ${(x + nx) / 2} ${inset + (i % 2 ? amp : -amp)}, ${nx} ${inset}`;
  }
  for (let y = inset; y < h - inset; y += step, i++) {
    const ny = Math.min(y + step, h - inset);
    d += ` Q ${w - inset + (i % 2 ? -amp : amp)} ${(y + ny) / 2}, ${w - inset} ${ny}`;
  }
  for (let x = w - inset; x > inset; x -= step, i++) {
    const nx = Math.max(x - step, inset);
    d += ` Q ${(x + nx) / 2} ${h - inset + (i % 2 ? -amp : amp)}, ${nx} ${h - inset}`;
  }
  for (let y = h - inset; y > inset; y -= step, i++) {
    const ny = Math.max(y - step, inset);
    d += ` Q ${inset + (i % 2 ? amp : -amp)} ${(y + ny) / 2}, ${inset} ${ny}`;
  }
  return d + " Z";
}

const SEAL_IMG = "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/ff1bxyqt_Photoroom-20260619_125322069%20%281%29%20%281%29.png";

function printAddressLabels(chosen) {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const addrLines = (a) => !a ? [] : typeof a === "string" ? [a]
    : [[a.line1, a.line2].filter(Boolean).join(", "), [a.suburb, a.city].filter(Boolean).join(", "), [a.postcode, a.region].filter(Boolean).join(" ")].filter(Boolean);

  const W = 420, H = 280;
  const frame = `<svg class="frame" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none"><path d="${wavyRect(W, H)}" fill="none" stroke="#4A2545" stroke-width="5" stroke-linecap="round"/></svg>`;

  const labels = chosen.flatMap(([cat, os]) => os.map((o) => {
    const lines = addrLines(o.address);
    return `
    <div class="label">
      ${frame}
      <div class="ref">${esc(cat)} · ${o.order_number ? "#" + esc(o.order_number) : "#" + esc(String(o.id || "").slice(0, 8))}</div>
      <div class="inner">
        <div class="to-row"><span class="to">To:</span><span class="name">${esc(o.customer_name || o.email || "")}</span></div>
        <div class="divider"></div>
        <div class="addr-row"><svg class="pin" viewBox="0 0 24 24" fill="none" stroke="#4A2545" stroke-width="2.4"><path d="M12 21s-7-6.1-7-11a7 7 0 1 1 14 0c0 4.9-7 11-7 11z"/><circle cx="12" cy="10" r="2.6" fill="#4A2545" stroke="none"/></svg><span class="addr">${esc(lines.join(", ") || "No address on file , check the order")}</span></div>
        <div class="seal-row">
          <img class="seal" src="${SEAL_IMG}" alt="Heritage Dahlias · Est 1932"/>
        </div>
      </div>
    </div>`;
  })).join("");

  const html = `<!doctype html><html><head><title>Address labels</title><style>
    @page{size:A4;margin:0;}
    body{font-family:Georgia,'Times New Roman',serif;color:#4A2545;margin:5mm;background:#fff;}
    .grid{display:grid;grid-template-columns:100mm 100mm;gap:0;}
    .label{position:relative;background:#fff;width:100mm;height:70mm;border-radius:0;padding:8mm 9mm 5mm;page-break-inside:avoid;box-sizing:border-box;overflow:hidden;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .frame{position:absolute;inset:0;width:100%;height:100%;}
    .ref{position:absolute;top:4.5mm;right:8mm;font-size:8px;letter-spacing:.18em;text-transform:uppercase;color:#4A2545;opacity:.55;}
    .inner{position:relative;}
    .to-row{display:flex;align-items:baseline;gap:10px;margin-top:1.5mm;}
    .to{font-style:italic;font-size:24px;font-weight:800;}
    .name{font-size:22px;font-weight:900;letter-spacing:.02em;}
    .divider{border-top:2.4px solid #4A2545;margin:4mm 0 4.5mm;}
    .addr-row{display:flex;align-items:flex-start;gap:9px;}
    .pin{width:22px;height:22px;flex:none;margin-top:2px;}
    .addr{font-size:18px;font-weight:800;line-height:1.45;}
    .seal-row{display:flex;align-items:center;justify-content:center;margin-top:4.5mm;}
    .seal{width:19mm;height:19mm;object-fit:contain;flex:none;}
    .rule{display:none;}
    .rule{flex:1;height:0;border-top:1.6px solid #4A2545;position:relative;}
    .rule.left::before{content:"";position:absolute;left:-1px;top:-4px;border:3.5px solid transparent;border-right:7px solid #4A2545;}
    .rule.left::after{content:"";position:absolute;right:-1px;top:-3.5px;width:6px;height:6px;background:#4A2545;transform:rotate(45deg);}
    .rule.right::after{content:"";position:absolute;right:-1px;top:-4px;border:3.5px solid transparent;border-left:7px solid #4A2545;}
    .rule.right::before{content:"";position:absolute;left:-1px;top:-3.5px;width:6px;height:6px;background:#4A2545;transform:rotate(45deg);}
    @media print{.noprint{display:none;}}
  </style></head><body>
    <div class="noprint" style="text-align:right;margin-bottom:8px"><button onclick="window.print()" style="padding:8px 18px;cursor:pointer;">Print</button></div>
    <div class="grid">${labels}</div>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

function printPackingSummary(chosen, mode = "both") {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const addr = (a) => !a ? "" : typeof a === "string" ? a
    : [a.line1, a.line2, a.suburb, a.city, a.postcode, a.region].filter(Boolean).join(", ");

  const totalOrders = chosen.reduce((n, [, os]) => n + os.length, 0);

  const boxRows = chosen.map(([c, os]) =>
    `<tr><td><strong>${esc(c)}</strong></td><td class="qty">${os.length}</td></tr>`).join("");

  const tubers = new Map();
  for (const [, os] of chosen) for (const o of os) for (const li of ((o.tubers && o.tubers.length) ? o.tubers : (o.line_items || []))) {
    const k = li.name || "Item";
    tubers.set(k, (tubers.get(k) || 0) + Number(li.qty || 1));
  }
  const tuberRows = [...tubers.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([n, q]) => `<tr><td>${esc(n)}</td><td class="qty">${q}</td></tr>`).join("");

  const sections = chosen.map(([c, os]) => `
    <h3>${esc(c)} , ${os.length} to pack</h3>
    ${os.map((o) => {
      const paid = o.payment_status === "paid";
      const addrText = addr(o.address);
      const rural = /\bRD\s?\d|\brural\b/i.test(addrText) || o.rural_delivery;
      const local = o.stage === "delivery" || o.delivery_method === "local" || /local/i.test(String(o.shipping_provider || ""));
      return `
      <div class="slip">
        <div class="head"><strong>☐ ${esc(o.customer_name || o.email || "Unknown")}</strong>
          ${o.order_number ? `<span>#${esc(o.order_number)}</span>` : `<span>#${esc(String(o.id || "").slice(0, 8))}</span>`}
          <span class="tag ${paid ? "ok" : "warn"}">${esc(o.payment_status || "unpaid")}</span>
          ${o.kind ? `<span class="tag kind">${esc(String(o.kind).replace(/_/g, " "))}${o.mystery_tier ? " · " + esc(o.mystery_tier) : ""}</span>` : ""}
          <span class="tag ${local ? "says" : "kind"}">${local ? "LOCAL DELIVERY" : "SHIPPING"}</span>
          ${rural ? `<span class="tag warn">RURAL</span>` : ""}
          ${o.customer_says_paid ? `<span class="tag says">customer says paid</span>` : ""}
          ${o.hq_pick_count ? `<span class="pick">${(o.hq_picked_variety_ids || []).length ? "picks in" : "AWAITING PICKS"}</span>` : ""}
          <span class="total">NZ$${Number(o.total || 0).toFixed(2)}</span>
          <span class="sticker">ticket / receipt sticker</span>
        </div>
        <div class="contact">${[o.email, o.phone].filter(Boolean).map(esc).join(" · ")}${o.created_at ? ` · ordered ${esc(new Date(o.created_at).toLocaleDateString("en-NZ"))}` : ""}</div>
        ${addrText ? `<div class="addr">${esc(addrText)}</div>` : `<div class="addr missing">No address on file</div>`}
        <ul>${(o.line_items || []).map((li) => `<li>${li.qty}× ${esc(li.name)}${li.unit_price ? ` , $${Number(li.unit_price).toFixed(2)} ea` : ""}</li>`).join("")}</ul>
        ${(o.tubers || []).length ? `<div class="tubers"><strong>Tubers to pull (${o.tubers.reduce((n, t) => n + Number(t.qty || 0), 0)}):</strong> ${o.tubers.map((t) => `☐ ${t.qty}× ${esc(t.name)}`).join(" &nbsp; ")}</div>` : (o.hq_pick_count ? `<div class="tubers missing">Tubers not confirmed yet , customer still to pick ${o.hq_pick_count}.</div>` : "")}
        ${Number(o.box_remaining || 0) > 0 ? `<div class="tubers missing">Box contents still to choose: ${o.box_remaining} tuber${o.box_remaining === 1 ? "" : "s"} (not yet named)</div>` : ""}
        ${(o.other_items || []).length ? `<div class="note">Other items to pack: ${o.other_items.map((t) => `☐ ${t.qty}× ${esc(t.name)}`).join(" &nbsp; ")}</div>` : ""}
        ${o.hq_picks_note ? `<div class="note">HQ note: ${esc(o.hq_picks_note)}</div>` : ""}
        ${o.notes ? `<div class="note">Order notes: ${esc(o.notes)}</div>` : ""}
        ${o.admin_notes ? `<div class="note admin">Admin notes: ${esc(o.admin_notes)}</div>` : ""}
        ${o.tracking_number ? `<div class="note">Tracking: ${esc(o.tracking_number)}</div>` : ""}
      </div>`;
    }).join("")}`).join("");

  const invoicePages = chosen.flatMap(([c, os]) => os.map((o) => {
    const items = o.line_items || [];
    const itemRows = items.map((li) => {
      const qty = Number(li.qty || 1);
      const unit = Number(li.unit_price || 0);
      return `<tr><td>${esc(li.name)}</td><td class="qty">${qty}</td><td class="money">$${unit.toFixed(2)}</td><td class="money">$${(qty * unit).toFixed(2)}</td></tr>`;
    }).join("");
    const itemsTotal = items.reduce((n, li) => n + Number(li.qty || 1) * Number(li.unit_price || 0), 0);
    const shipping = Number(o.shipping_price || 0);
    const total = Number(o.total || 0) > 0 ? Number(o.total) : itemsTotal + shipping;
    const paid = o.payment_status === "paid";
    const number = o.order_number ? `#${esc(o.order_number)}` : `#${esc(String(o.id || "").slice(0, 8))}`;
    return `
    <div class="invoice">
      <svg class="inv-frame" viewBox="0 0 760 1050" preserveAspectRatio="none"><path d="${wavyRect(760, 1050, 5, 32, 9)}" fill="none" stroke="#4A2545" stroke-width="5" stroke-linecap="round"/></svg>
      <div class="inv-inner">
      <div class="inv-seal-row"><span class="inv-rule left"></span><img class="inv-seal" src="${SEAL_IMG}" alt="Heritage Dahlias"/><span class="inv-rule right"></span></div>
      <div class="inv-head">
        <div>
          <div class="inv-brand">HERITAGE DAHLIAS · EST 1932</div>
          <h1>Invoice</h1>
          <div class="meta">${number} · ${esc(c)} queue · ${new Date(o.created_at || Date.now()).toLocaleDateString("en-NZ")}</div>
        </div>
        <div class="stamp ${paid ? "paid" : "unpaid"}">${paid ? "PAID" : "UNPAID"}</div>
      </div>
      <div class="inv-to">
        <strong>${esc(o.customer_name || "Unknown")}</strong><br/>
        ${o.email ? `${esc(o.email)}<br/>` : ""}
        ${o.phone ? `${esc(o.phone)}<br/>` : ""}
        ${addr(o.address) ? esc(addr(o.address)) : ""}
      </div>
      <table class="inv-table">
        <tr><th>Item</th><th class="qty">Qty</th><th class="money">Unit</th><th class="money">Line</th></tr>
        ${itemRows}
        ${shipping > 0 ? `<tr><td colspan="3" class="label">Shipping</td><td class="money">$${shipping.toFixed(2)}</td></tr>` : ""}
        <tr class="total"><td colspan="3" class="label"><strong>Total NZD</strong></td><td class="money"><strong>$${total.toFixed(2)}</strong></td></tr>
      </table>
      ${!paid ? `<div class="inv-pay">Pay via bank transfer: <strong>HERITAGE DAHLIAS NZ LIMITED</strong> · 38-9027-0833898-00 · Reference: <strong>${number.replace("#", "")}</strong></div>` : ""}
      <div class="inv-foot">Heritage Dahlias NZ Limited · heritagedahlias.nz</div>
      </div>
    </div>`;
  })).join("");

  const html = `<!doctype html><html><head><title>Packing summary</title><style>
    body{font-family:Georgia,'Times New Roman',serif;color:#3C2A1E;margin:32px;}
    h1{font-size:22px;margin:0;} h2{font-size:14px;letter-spacing:.2em;text-transform:uppercase;margin:26px 0 8px;border-bottom:2px solid #C9A268;padding-bottom:4px;}
    h3{font-size:15px;margin:22px 0 8px;color:#6E1E3A;page-break-after:avoid;}
    .meta{font-size:12px;opacity:.7;margin-top:4px;}
    table{border-collapse:collapse;font-size:13px;min-width:340px;}
    td{border:1px solid #E5D9C5;padding:5px 10px;} .qty{text-align:center;font-weight:bold;width:60px;}
    .slip{border:1px solid #E5D9C5;border-radius:6px;padding:8px 12px;margin-bottom:8px;page-break-inside:avoid;font-size:13px;}
    .head{display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;}
    .head .total{margin-left:auto;font-weight:bold;font-size:13px;}
    .contact{font-size:10.5px;opacity:.7;margin-top:2px;}
    .slip .tag{font-size:9px;letter-spacing:.12em;text-transform:uppercase;border-radius:8px;padding:1px 8px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .slip .tag.ok{background:#2F6B4F;color:#fff;} .slip .tag.warn{background:#F4D06F;color:#6E1E3A;}
    .slip .tag.kind{background:#EFE6F0;color:#3E1F46;} .slip .tag.says{background:#A33030;color:#fff;}
    .slip .sticker{margin-left:auto;display:inline-block;width:62mm;height:16mm;border:1.5px dashed #9C8B73;border-radius:4px;font-size:8px;color:#9C8B73;text-align:center;line-height:16mm;text-transform:uppercase;letter-spacing:2px;}
    .addr{font-size:11px;opacity:.75;margin-top:2px;}
    .addr.missing{color:#A33030;font-style:italic;opacity:1;}
    .slip ul{margin:5px 0 3px;padding-left:18px;}
    .tubers{margin:4px 0;padding:5px 8px;border:1px solid #C9A268;background:#FBF6E9;font-size:12px;}
    .tubers.missing{border-style:dashed;font-style:italic;color:#8A5D12;}
    .note{font-size:11px;font-style:italic;color:#6E1E3A;}
    .note.admin{color:#A05A20;}
    .pick{font-size:10px;background:#FBF0E0;color:#A05A20;border-radius:8px;padding:1px 8px;}
    .invoice{page-break-before:always;position:relative;background:#fff;color:#4A2545;border-radius:6px;padding:12mm 14mm;min-height:255mm;box-sizing:border-box;overflow:hidden;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .inv-frame{position:absolute;inset:0;width:100%;height:100%;}
    .inv-inner{position:relative;}
    .inv-seal-row{display:flex;align-items:center;gap:12px;margin-bottom:6mm;}
    .inv-seal{width:26mm;height:26mm;object-fit:contain;flex:none;}
    .inv-rule{flex:1;height:0;border-top:1.6px solid #4A2545;position:relative;}
    .inv-rule.left::before{content:"";position:absolute;left:-1px;top:-4px;border:3.5px solid transparent;border-right:7px solid #4A2545;}
    .inv-rule.left::after{content:"";position:absolute;right:-1px;top:-3.5px;width:6px;height:6px;background:#4A2545;transform:rotate(45deg);}
    .inv-rule.right::after{content:"";position:absolute;right:-1px;top:-4px;border:3.5px solid transparent;border-left:7px solid #4A2545;}
    .inv-rule.right::before{content:"";position:absolute;left:-1px;top:-3.5px;width:6px;height:6px;background:#4A2545;transform:rotate(45deg);}
    .inv-head{display:flex;justify-content:space-between;align-items:flex-start;}
    .inv-brand{font-size:10px;letter-spacing:.35em;color:#4A2545;opacity:.85;}
    .invoice h1{font-size:28px;margin:6px 0 2px;color:#4A2545;font-style:italic;}
    .invoice .meta{color:#4A2545;opacity:.7;}
    .stamp{font-size:20px;font-weight:bold;letter-spacing:.2em;border:3px solid;border-radius:6px;padding:6px 16px;transform:rotate(-4deg);}
    .stamp.paid{color:#2F6B4F;border-color:#2F6B4F;}
    .stamp.unpaid{color:#8E2323;border-color:#8E2323;}
    .inv-to{margin:8mm 0 6mm;font-size:14px;line-height:1.5;}
    .inv-table{width:100%;border-collapse:collapse;font-size:13px;}
    .inv-table th{text-align:left;border-bottom:2px solid #4A2545;padding:6px 10px;font-size:11px;letter-spacing:.15em;text-transform:uppercase;color:#4A2545;}
    .inv-table td{border-bottom:1px solid #4A254555;padding:6px 10px;}
    .inv-table .money{text-align:right;width:80px;} .inv-table .label{text-align:right;}
    .inv-table .total td{border-top:2px solid #4A2545;border-bottom:none;font-size:15px;}
    .inv-pay{margin-top:6mm;font-size:12px;border:1.5px solid #4A2545;border-left:5px solid #4A2545;padding:8px 12px;}
    .inv-foot{margin-top:10mm;font-size:10px;opacity:.6;text-align:center;letter-spacing:.15em;text-transform:uppercase;}
    @media print{.noprint{display:none;}}
  </style></head><body>
    <div class="noprint" style="text-align:right"><button onclick="window.print()" style="padding:8px 18px;cursor:pointer;">Print</button></div>
    <h1>Heritage Dahlias , Packing Summary</h1>
    <div class="meta">${totalOrders} order${totalOrders === 1 ? "" : "s"} across ${chosen.length} categor${chosen.length === 1 ? "y" : "ies"} · ${new Date().toLocaleString("en-NZ")}</div>
    ${mode !== "invoices" ? `
    <h2>Boxes to pack</h2>
    <table>${boxRows}<tr><td><strong>Total</strong></td><td class="qty">${totalOrders}</td></tr></table>
    <h2>Items to pull</h2>
    <table>${tuberRows}</table>
    <h2>Orders</h2>
    ${sections}` : ""}
    ${mode !== "sheets" ? `
    <h2 style="page-break-before:always;">Invoices , one per order</h2>
    ${invoicePages}` : ""}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

/* Per-order prints , used by the Packing/Shipping order rows. */
export function printOrderSheet(order) {
  printPackingSummary([[categoryOf(order), [order]]], "sheets");
}

export function printOrderInvoice(order) {
  printPackingSummary([[categoryOf(order), [order]]], "invoices");
}

export function printOrderLabel(order) {
  printAddressLabels([[categoryOf(order), [order]]]);
}
